import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import Home from './pages/Home'
import CardInfo from './pages/CardInfo'
import Login from './pages/Login'
import LoginMobile from './pages/LoginMobile'
import Dashboard from './pages/Dashboard'
import DashboardMobile from './pages/DashboardMobile'
import History from './pages/HistorySection'
import Profile from './pages/Profile'
import Aboutus from './pages/Aboutus'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Vacancies from './pages/Vacancies'
import Business from './pages/Business'
import CrmRequest from './pages/CrmRequest'
import Cart from './pages/Cart'
import TrackOrder from './pages/TrackOrder'
import News from './pages/News'

import CoffeeDashboard from './pages/CoffeeDashboard'
import AuthScreens from './pages/AuthScreens'
import { LangProvider } from './context/LangContext'
import UserTracker from './components/UserTracker'

// Глобальная защита от копирования текста и сохранения изображений
const GLOBAL_PROTECTION = `
  * {
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
    -webkit-touch-callout: none !important;
  }
  img {
    -webkit-user-drag: none !important;
    -khtml-user-drag: none !important;
    -moz-user-drag: none !important;
    -o-user-drag: none !important;
    user-drag: none !important;
    pointer-events: none !important;
  }
`

function App() {
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  // Глобальная защита от копирования текста, сохранения изображений и контекстного меню
  useEffect(() => {
    // Вставляем CSS-защиту в head
    const styleTag = document.createElement('style')
    styleTag.id = 'global-copy-protection'
    styleTag.textContent = GLOBAL_PROTECTION
    document.head.appendChild(styleTag)

    // Защита от копирования
    const preventCopy = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }

    // Защита от контекстного меню (правая кнопка мыши)
    const preventContextMenu = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }

    // Защита от перетаскивания изображений
    const preventDrag = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    // Защита от выделения через клавиатуру (Ctrl+A, Cmd+A)
    const preventSelectAll = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        e.stopPropagation()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault()
        e.stopPropagation()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        e.stopPropagation()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault()
        e.stopPropagation()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    // Добавляем обработчики событий на уровне документа
    document.addEventListener('copy', preventCopy, true)
    document.addEventListener('cut', preventCopy, true)
    document.addEventListener('contextmenu', preventContextMenu, true)
    document.addEventListener('dragstart', preventDrag, true)
    document.addEventListener('keydown', preventSelectAll, true)

    // Очистка при размонтировании
    return () => {
      const existingStyle = document.getElementById('global-copy-protection')
      if (existingStyle) existingStyle.remove()
      document.removeEventListener('copy', preventCopy, true)
      document.removeEventListener('cut', preventCopy, true)
      document.removeEventListener('contextmenu', preventContextMenu, true)
      document.removeEventListener('dragstart', preventDrag, true)
      document.removeEventListener('keydown', preventSelectAll, true)
    }
  }, [])

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      // Расширенная проверка: ширина или наличие тача (чтобы точно ловить телефоны)
      setIsMobile(window.innerWidth <= 1024 || navigator.maxTouchPoints > 0)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    // Проверяем сохраненную сессию
    const savedCustomer = localStorage.getItem('qaraa_customer')
    if (savedCustomer) {
      try {
        setCustomer(JSON.parse(savedCustomer))
      } catch (error) {
        console.error('Error parsing saved customer:', error)
        localStorage.removeItem('qaraa_customer')
      }
    }
    setLoading(false)
  }, [])

  const handleLogin = (customerData) => {
    setCustomer(customerData)
    localStorage.setItem('qaraa_customer', JSON.stringify(customerData))
  }

  const handleLogout = async () => {
    if (customer?.id) {
      try {
        await supabase
          .from('customers')
          .update({ is_online: false, last_active: new Date().toISOString() })
          .eq('id', customer.id)
      } catch (e) {
        console.error('Logout update error:', e)
      }
    }
    // Only clear session after attempting update
    setCustomer(null)
    localStorage.removeItem('qaraa_customer')

    // Also sign out from Supabase auth if using it
    await supabase.auth.signOut()
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#FFFFFF',
          letterSpacing: '2px'
        }}>
          qaraa
        </div>
      </div>
    )
  }

  return (
    <LangProvider>
      <Router>
        <UserTracker customer={customer} onLogout={handleLogout} />
        <Routes>
          <Route path="/" element={customer ? <Navigate to="/dashboard" /> : <Home />} />
          <Route path="/card-info" element={customer ? <Navigate to="/dashboard" /> : <CardInfo />} />
          <Route path="/about-us" element={customer ? <Navigate to="/dashboard" /> : <Aboutus />} />
          <Route path="/business" element={<Business />} />
          <Route path="/vacancies" element={customer ? <Navigate to="/dashboard" /> : <Vacancies />} />
          <Route path="/privacy" element={customer ? <Navigate to="/dashboard" /> : <Privacy />} />
          <Route path="/terms" element={customer ? <Navigate to="/dashboard" /> : <Terms />} />
          <Route
            path="/login"
            element={<Navigate to="/auth" replace />}
          />
          <Route
            path="/dashboard"
            element={
              customer ? (
                isMobile ? <DashboardMobile /> : <Dashboard />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/history"
            element={
              customer ? <History customer={customer} /> : <Navigate to="/" />
            }
          />
          <Route
            path="/cart"
            element={
              customer ? <Cart customer={customer} onLogout={handleLogout} /> : <Navigate to="/" />
            }
          />
          <Route
            path="/track"
            element={
              customer ? <TrackOrder /> : <Navigate to="/" />
            }
          />
          <Route
            path="/profile"
            element={
              customer ? <Profile customer={customer} onLogout={handleLogout} /> : <Navigate to="/" />
            }
          />
          <Route
            path="/news"
            element={
              customer ? <News customer={customer} /> : <Navigate to="/" />
            }
          />

          <Route
            path="/coffeedashboard"
            element={
              customer ? <CoffeeDashboard /> : <Navigate to="/" />
            }
          />
          <Route
            path="/storedashboard"
            element={
              customer ? <Dashboard /> : <Navigate to="/" />
            }
          />
          <Route path="/auth" element={customer ? <Navigate to="/dashboard" /> : <AuthScreens onLogin={handleLogin} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </LangProvider>
  )
}

export default App
